// AdminOrgs — Admin UI for managing organizations and their branding.
// Route: /app#/admin/orgs

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import './AdminOrgs.css';

export default function AdminOrgs() {
  const { getAccessTokenSilently } = useAuth0();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingOrg, setEditingOrg] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const fetchOrgs = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const resp = await fetch('/api/v1/orgs/admin/list', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        if (resp.status === 403) {
          setError('Admin access required');
        } else {
          setError('Failed to fetch organizations');
        }
        return;
      }
      const data = await resp.json();
      setOrgs(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  if (loading) {
    return <div className="admin-orgs-loading">Loading organizations...</div>;
  }

  if (error) {
    return <div className="admin-orgs-error">{error}</div>;
  }

  return (
    <div className="admin-orgs">
      <div className="admin-orgs-header">
        <h1>Organizations</h1>
        <button 
          className="admin-orgs-create-btn"
          onClick={() => setShowCreateForm(true)}
        >
          + Create Organization
        </button>
      </div>

      {showCreateForm && (
        <CreateOrgForm 
          onClose={() => setShowCreateForm(false)}
          onCreated={() => { setShowCreateForm(false); fetchOrgs(); }}
          getToken={getAccessTokenSilently}
        />
      )}

      <div className="admin-orgs-list">
        {orgs.map(org => (
          <OrgCard 
            key={org.id} 
            org={org} 
            onEdit={() => setEditingOrg(org)}
            isEditing={editingOrg?.id === org.id}
            onClose={() => setEditingOrg(null)}
            onUpdated={() => { setEditingOrg(null); fetchOrgs(); }}
            getToken={getAccessTokenSilently}
          />
        ))}
      </div>
    </div>
  );
}

function OrgCard({ org, onEdit, isEditing, onClose, onUpdated, getToken }) {
  const subdomain = org.slug ? `${org.slug}.seidr.makers.bot` : null;
  
  return (
    <div className="org-card">
      <div className="org-card-header">
        <div className="org-card-info">
          <h3>{org.display_name}</h3>
          <span className="org-card-slug">/{org.slug}</span>
        </div>
        <div className="org-card-actions">
          {subdomain && (
            <a 
              href={`https://${subdomain}/app`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="org-card-link"
            >
              Preview →
            </a>
          )}
          <button onClick={onEdit} className="org-card-edit-btn">
            Edit
          </button>
        </div>
      </div>
      
      <div className="org-card-branding">
        <div 
          className="org-card-color" 
          style={{ background: org.branding?.primary_color || '#1a73e8' }}
          title="Primary color"
        />
        <div 
          className="org-card-color" 
          style={{ background: org.branding?.secondary_color || '#34a853' }}
          title="Secondary color"
        />
        <span className="org-card-company">
          {org.branding?.company_name || '—'}
        </span>
      </div>

      {isEditing && (
        <EditOrgForm 
          org={org}
          onClose={onClose}
          onUpdated={onUpdated}
          getToken={getToken}
        />
      )}
    </div>
  );
}

function CreateOrgForm({ onClose, onCreated, getToken }) {
  const [slug, setSlug] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [auth0OrgId, setAuth0OrgId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    
    try {
      const token = await getToken();
      const resp = await fetch('/api/v1/orgs/admin/create', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          slug,
          display_name: displayName,
          auth0_org_id: auth0OrgId,
        }),
      });
      
      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.detail || 'Failed to create organization');
      }
      
      onCreated();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="org-form-overlay" onClick={onClose}>
      <form className="org-form" onClick={e => e.stopPropagation()} onSubmit={handleSubmit}>
        <h2>Create Organization</h2>
        
        {error && <div className="org-form-error">{error}</div>}
        
        <label>
          Slug (subdomain)
          <input 
            type="text" 
            value={slug} 
            onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            placeholder="e.g., vannir"
            required
          />
          <span className="org-form-hint">{slug}.seidr.makers.bot</span>
        </label>
        
        <label>
          Display Name
          <input 
            type="text" 
            value={displayName} 
            onChange={e => setDisplayName(e.target.value)}
            placeholder="e.g., Vannir Energy"
            required
          />
        </label>
        
        <label>
          Auth0 Org ID
          <input 
            type="text" 
            value={auth0OrgId} 
            onChange={e => setAuth0OrgId(e.target.value)}
            placeholder="e.g., org_xxx"
            required
          />
        </label>
        
        <div className="org-form-actions">
          <button type="button" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" disabled={saving}>
            {saving ? 'Creating...' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
}

function EditOrgForm({ org, onClose, onUpdated, getToken }) {
  const [displayName, setDisplayName] = useState(org.display_name);
  const [companyName, setCompanyName] = useState(org.branding?.company_name || '');
  const [tagline, setTagline] = useState(org.branding?.tagline || '');
  const [primaryColor, setPrimaryColor] = useState(org.branding?.primary_color || '#1a73e8');
  const [secondaryColor, setSecondaryColor] = useState(org.branding?.secondary_color || '#34a853');
  const [logoFile, setLogoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    
    try {
      const token = await getToken();
      
      // Update branding
      const resp = await fetch(`/api/v1/orgs/admin/${org.slug}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          display_name: displayName,
          branding: {
            company_name: companyName,
            tagline,
            primary_color: primaryColor,
            secondary_color: secondaryColor,
          },
        }),
      });
      
      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.detail || 'Failed to update organization');
      }
      
      // Upload logo if selected
      if (logoFile) {
        const formData = new FormData();
        formData.append('file', logoFile);
        
        const logoResp = await fetch(`/api/v1/orgs/admin/${org.slug}/logo`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        
        if (!logoResp.ok) {
          throw new Error('Failed to upload logo');
        }
      }
      
      onUpdated();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="org-form-overlay" onClick={onClose}>
      <form className="org-form org-form-edit" onClick={e => e.stopPropagation()} onSubmit={handleSubmit}>
        <h2>Edit {org.display_name}</h2>
        
        {error && <div className="org-form-error">{error}</div>}
        
        <label>
          Display Name
          <input 
            type="text" 
            value={displayName} 
            onChange={e => setDisplayName(e.target.value)}
            required
          />
        </label>
        
        <label>
          Company Name (shown in UI)
          <input 
            type="text" 
            value={companyName} 
            onChange={e => setCompanyName(e.target.value)}
          />
        </label>
        
        <label>
          Tagline
          <input 
            type="text" 
            value={tagline} 
            onChange={e => setTagline(e.target.value)}
            placeholder="e.g., Nordic Site Intelligence"
          />
        </label>
        
        <div className="org-form-colors">
          <label>
            Primary Color
            <input 
              type="color" 
              value={primaryColor} 
              onChange={e => setPrimaryColor(e.target.value)}
            />
          </label>
          <label>
            Secondary Color
            <input 
              type="color" 
              value={secondaryColor} 
              onChange={e => setSecondaryColor(e.target.value)}
            />
          </label>
        </div>
        
        <label>
          Logo
          <input 
            type="file" 
            accept="image/*"
            onChange={e => setLogoFile(e.target.files[0])}
          />
          {org.branding?.logo_url && !logoFile && (
            <img src={org.branding.logo_url} alt="Current logo" className="org-form-logo-preview" />
          )}
        </label>
        
        <div className="org-form-actions">
          <button type="button" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
